import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { orderBy } from 'firebase/firestore';
import { motion } from 'motion/react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  CLOSING_TASK_STATUSES,
  TASK_PRIORITIES,
  TASK_STATUSES,
  canCloseTask,
  canComment,
  canProject,
  canResolveIssue,
  type Comment,
  type Issue,
  type ProjectMember,
  type Task,
  type TaskPriority,
  type TaskStatus,
} from '@allemnii-pulse/shared';
import { useCollection, useDocument } from '../../lib/firestore.js';
import { useUserProfiles, type UserProfile } from '../../lib/userProfiles.js';
import { useProject } from '../projects/ProjectProvider.js';
import { useAuth } from '../auth/AuthProvider.js';
import { api } from '../../lib/api.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { StatusBadge } from '../../components/ui/StatusBadge.js';
import { SourceBadge } from '../../components/ui/SourceBadge.js';
import { Term } from '../../components/ui/Term.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select.js';
import { Input } from '../../components/ui/Input.js';
import { UserName } from '../../components/UserName.js';
import { Dialog, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog.js';
import { EditTaskDialog } from './EditTaskDialog.js';

function nameFor(uid: string, profile?: UserProfile): string {
  return profile?.displayName?.trim() || profile?.email?.trim() || uid.slice(0, 6);
}

/** Matches an unterminated "@word" at the end of the comment draft, e.g. typing "@ah" — the
 * captured group is what's typed so far, used to filter the mention dropdown. */
const MENTION_QUERY_RE = /(?:^|\s)@([\p{L}\p{N}_-]*)$/u;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Renders a comment body as sanitized markdown. The literal "@Name" text inserted when a mention
 * was composed (see handleSelectMention) is highlighted — matched as an exact substring, not
 * re-parsed, so only tokens that correspond to a stored mentionedUid light up. Highlighting runs
 * on the text segments of the HTML so it can't touch tag attributes; the result is then sanitized. */
function CommentBody({
  body,
  mentionedUids,
  profiles,
}: {
  body: string;
  mentionedUids: string[];
  profiles: Record<string, UserProfile>;
}) {
  let html = marked.parse(body, { async: false, breaks: true, gfm: true });

  const tokens = mentionedUids
    .map((uid) => escapeHtml(`@${nameFor(uid, profiles[uid])}`))
    .filter((token) => html.includes(token));
  if (tokens.length > 0) {
    const pattern = new RegExp(`(${tokens.map(escapeRegExp).join('|')})`, 'g');
    html = html
      .split(/(<[^>]+>)/)
      .map((part) =>
        part.startsWith('<')
          ? part
          : part.replace(pattern, '<span class="font-medium text-accent">$1</span>'),
      )
      .join('');
  }

  // Sanitize last, after any HTML mutation above, so nothing unsanitized reaches the DOM.
  return (
    <div
      className="doc-prose break-words text-sm"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
}

export function TaskDetailPage() {
  const { projectId, project, role } = useProject();
  const { taskId } = useParams<{ taskId: string }>();
  const { user } = useAuth();
  const { data: task } = useDocument<Task>(taskId ? `projects/${projectId}/tasks/${taskId}` : null);
  const { data: members } = useCollection<ProjectMember>(`projects/${projectId}/members`);
  const memberProfiles = useUserProfiles(members.map((m) => m.uid));
  const { data: comments } = useCollection<Comment>(
    `projects/${projectId}/tasks/${taskId}/comments`,
    [orderBy('createdAt', 'asc')],
  );
  const { data: issues } = useCollection<Issue>(`projects/${projectId}/tasks/${taskId}/issues`, [
    orderBy('createdAt', 'desc'),
  ]);

  const [commentBody, setCommentBody] = useState('');
  const [mentions, setMentions] = useState<{ uid: string; token: string }[]>([]);
  const [issueTitle, setIssueTitle] = useState('');
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  if (!task) return <div className="p-4 text-sm text-muted sm:p-6">Loading…</div>;

  const uid = user?.uid ?? '';
  const isMine = task.assigneeUid === uid;
  const isUnassigned = !task.assigneeUid;
  const canEdit = canProject(role, 'task.edit');
  const canAssignAnyone = canProject(role, 'task.assign.any');
  const canDelete = canProject(role, 'task.delete');
  const canClose = canCloseTask(role, uid, task);
  const canCommentHere = !!project && canComment(role, project);
  // Done/canceled are the assignee's (or a lead's) call — see canCloseTask. Everyone else still
  // sees the task's current status in the list, so the select can always render its value.
  const statusOptions = TASK_STATUSES.filter(
    (s) =>
      canClose || s === task.status || !(CLOSING_TASK_STATUSES as readonly string[]).includes(s),
  );

  async function deleteTask() {
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/tasks/${taskId}`);
      navigate(`/projects/${projectId}/tasks`);
    } catch {
      setBusy(false);
    }
  }

  const mentionQuery = commentBody.match(MENTION_QUERY_RE)?.[1] ?? null;
  const mentionCandidates =
    mentionQuery === null
      ? []
      : members
          .filter((m) => m.uid !== user?.uid)
          .map((m) => ({ uid: m.uid, name: nameFor(m.uid, memberProfiles[m.uid]) }))
          .filter((m) => m.name.toLowerCase().includes(mentionQuery.toLowerCase()))
          .slice(0, 6);

  async function changeStatus(status: TaskStatus) {
    await api.patch(`/projects/${projectId}/tasks/${taskId}`, { status });
  }

  async function changePriority(priority: TaskPriority) {
    await api.patch(`/projects/${projectId}/tasks/${taskId}`, { priority });
  }

  async function toggleAssign() {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/tasks/${taskId}/assign`, {
        assigneeUid: isMine ? null : (user?.uid ?? null),
      });
    } finally {
      setBusy(false);
    }
  }

  async function assignTo(assigneeUid: string | null) {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/tasks/${taskId}/assign`, { assigneeUid });
    } finally {
      setBusy(false);
    }
  }

  function handleSelectMention(uid: string) {
    const name = nameFor(uid, memberProfiles[uid]);
    const token = `@${name}`;
    setCommentBody((body) =>
      body.replace(MENTION_QUERY_RE, (match) => `${match.startsWith(' ') ? ' ' : ''}${token} `),
    );
    setMentions((prev) => (prev.some((m) => m.uid === uid) ? prev : [...prev, { uid, token }]));
  }

  async function postComment() {
    if (!commentBody.trim()) return;
    setBusy(true);
    try {
      const body = commentBody.trim();
      await api.post(`/projects/${projectId}/tasks/${taskId}/comments`, {
        body,
        mentionedUids: mentions.filter((m) => body.includes(m.token)).map((m) => m.uid),
      });
      setCommentBody('');
      setMentions([]);
    } finally {
      setBusy(false);
    }
  }

  async function raiseIssue() {
    if (!issueTitle.trim()) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/tasks/${taskId}/issues`, {
        title: issueTitle.trim(),
        severity: 'medium',
      });
      setIssueTitle('');
      setShowIssueForm(false);
    } finally {
      setBusy(false);
    }
  }

  async function resolveIssue(issueId: string) {
    await api.patch(`/projects/${projectId}/tasks/${taskId}/issues/${issueId}`, {});
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4 sm:gap-6 sm:p-6">
      <Link to={`/projects/${projectId}/tasks`} className="text-xs text-muted hover:text-text">
        ← Back to tasks
      </Link>

      {issues.some((i) => i.status === 'open') && (
        <Card className="border-danger/40 bg-[color-mix(in_srgb,var(--danger)_8%,var(--surface))] p-4">
          <span className="text-sm font-semibold text-danger">
            <Term id="blocked" /> — {issues.find((i) => i.status === 'open')?.title}
          </span>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={task.status} />
        <SourceBadge source={task.source} />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select
            value={task.status}
            disabled={!canEdit}
            onValueChange={(v) => void changeStatus(v as TaskStatus)}
          >
            <SelectTrigger className="h-10 w-auto text-xs md:h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted">Priority</span>
          <Select
            value={task.priority}
            disabled={!canEdit}
            onValueChange={(v) => void changePriority(v as TaskPriority)}
          >
            <SelectTrigger className="h-10 w-auto text-xs md:h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TASK_PRIORITIES.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canEdit && (
            <Button className="h-10 text-xs md:h-8" onClick={() => setShowEditDialog(true)}>
              Edit details
            </Button>
          )}
          {canProject(role, 'issue.raise') && (
            <Button
              variant="destructive"
              className="h-10 text-xs md:h-8"
              onClick={() => setShowIssueForm((v) => !v)}
            >
              Raise an issue
            </Button>
          )}
          {canDelete && (
            <Button
              variant="destructive"
              className="h-10 text-xs md:h-8"
              onClick={() => setShowDeleteDialog(true)}
            >
              Delete task
            </Button>
          )}
        </div>
      </div>

      <Card className="flex flex-col gap-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 break-words">
            <h1 className="font-heading font-semibold text-xl">{task.title}</h1>
            {task.description && (
              <p className="doc-prose mt-2 text-sm text-muted">{task.description}</p>
            )}
          </div>
        </div>

        {task.acceptanceCriteria.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">
              Acceptance criteria
            </span>
            <ul className="flex flex-col gap-1 text-sm">
              {task.acceptanceCriteria.map((c, i) => (
                <li key={i} className="flex gap-2 wrap-anywhere">
                  <span className="text-success">✓</span>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 border-t border-border pt-4 text-sm">
          <span className="text-muted">
            Assignee:{' '}
            <span className="text-text">
              {task.assigneeUid ? <UserName uid={task.assigneeUid} avatar /> : 'Unassigned'}
            </span>
          </span>
          {canAssignAnyone ? (
            <Select
              value={task.assigneeUid ?? 'unassigned'}
              onValueChange={(v) => void assignTo(v === 'unassigned' ? null : v)}
              disabled={busy}
            >
              <SelectTrigger className="h-10 w-auto text-xs md:h-8">
                <SelectValue placeholder="Assign to…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.uid} value={m.uid}>
                    <UserName uid={m.uid} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            canProject(role, 'task.claim') &&
            (isMine || isUnassigned) && (
              <Button className="h-10 md:h-8" disabled={busy} onClick={() => void toggleAssign()}>
                {isMine ? 'Release' : 'Self-assign'}
              </Button>
            )
          )}
        </div>

        {showIssueForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="flex gap-2 border-t border-border pt-4"
          >
            <Input
              value={issueTitle}
              onChange={(e) => setIssueTitle(e.target.value)}
              placeholder="What's blocking this?"
              className="h-10 flex-1 md:h-9"
            />
            <Button
              variant="default"
              className="h-10 md:h-8"
              disabled={busy}
              onClick={() => void raiseIssue()}
            >
              Raise
            </Button>
          </motion.div>
        )}
      </Card>

      {issues.length > 0 && (
        <Card className="flex flex-col divide-y divide-border">
          {issues.map((issue) => (
            <motion.div layout key={issue.id} className="flex items-center gap-3 p-4">
              <div className="min-w-0 break-words">
                <p
                  className={`text-sm font-medium ${issue.status === 'open' ? 'text-danger' : 'text-muted line-through'}`}
                >
                  {issue.title}
                </p>
                {issue.body && <p className="text-xs text-muted">{issue.body}</p>}
              </div>
              {issue.status === 'open' && canResolveIssue(role, uid, issue, task) && (
                <Button
                  className="ml-auto h-10 flex-shrink-0 md:h-8"
                  onClick={() => void resolveIssue(issue.id)}
                >
                  Resolve
                </Button>
              )}
            </motion.div>
          ))}
        </Card>
      )}

      <Card className="flex flex-col gap-4 p-4 sm:p-6">
        <h2 className="text-sm font-semibold">Comments</h2>
        <div className="flex flex-col gap-3">
          {comments.map((c) => (
            <motion.div
              layout
              key={c.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col gap-1 border-b border-border pb-3 last:border-none"
            >
              <div className="flex items-center gap-2 text-xs text-muted">
                <UserName uid={c.authorUid} avatar className="font-medium text-text" />
                {c.statusTag && (
                  <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px]">
                    {c.statusTag}
                  </span>
                )}
              </div>
              <CommentBody
                body={c.body}
                mentionedUids={c.mentionedUids}
                profiles={memberProfiles}
              />
            </motion.div>
          ))}
        </div>
        {!canCommentHere ? (
          <p className="text-xs text-muted">Viewers can't comment on this project.</p>
        ) : (
          <div className="flex gap-2">
            <div className="relative flex-1">
              {mentionCandidates.length > 0 && (
                <div className="absolute bottom-full left-0 mb-1 w-56 rounded-lg border border-border bg-surface p-1 shadow-lg">
                  {mentionCandidates.map((m) => (
                    <button
                      key={m.uid}
                      type="button"
                      // onPointerDown (not onClick) fires before the input blurs, so the selection
                      // still lands correctly even though tapping this button moves focus away.
                      onPointerDown={(e) => {
                        e.preventDefault();
                        handleSelectMention(m.uid);
                      }}
                      className="flex w-full items-center rounded-md px-2 py-2.5 text-left text-sm hover:bg-surface-2 md:py-1.5"
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              )}
              <Input
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                placeholder="Write a comment… use @ to mention someone"
                className="h-10 w-full md:h-9"
              />
            </div>
            <Button
              variant="default"
              className="h-10 md:h-8"
              disabled={busy}
              onClick={() => void postComment()}
            >
              Comment
            </Button>
          </div>
        )}
      </Card>

      <Dialog open={showDeleteDialog} onOpenChange={(o) => !o && setShowDeleteDialog(false)}>
        <DialogHeader>
          <DialogTitle>Delete this task?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted">
          “{task.title}” and its comments and issues will be removed permanently.
        </p>
        <DialogFooter>
          <Button type="button" onClick={() => setShowDeleteDialog(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={busy}
            onClick={() => void deleteTask()}
          >
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </Dialog>

      {showEditDialog && (
        <EditTaskDialog
          projectId={projectId}
          task={task}
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
        />
      )}
    </div>
  );
}
